import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../styles/colors';
import { TextTiny } from './Text';
import { FlexboxCenter } from './Layout';

const SquareWrapper = styled.div`
  position: relative;
  padding-top: 100%;
  align-self: stretch;
  
  & > * {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    overflow: hidden;
  }
`;

const ChartView = styled.div<{ animationRunning?: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: visible;
  
  background: conic-gradient(      
     ${Colors.secondary} 0, ${Colors.secondary} calc(100% * var(--anim-pie-chart, 1)), #eee 0
    // ${Colors.secondary} 0, ${Colors.secondary} 33%,
    // ${Colors.tertiary} 0, ${Colors.tertiary} 55%,
    // ${Colors.primary} 0, ${Colors.primary} 100%
  );
  
  animation: animPieChart 2s ${({ animationRunning = false }) => animationRunning ? 'running' : 'paused' };
  animation-fill-mode: forwards;
  animation-timing-function: ease-in-out;
  
  @keyframes animPieChart {   
    ${(() => {
      let res = '';
      for (let i=0; i<=100; i++) {
        res += `
          ${i}% {
            --anim-pie-chart: ${1 - Math.pow(1 - (.01 * i), 3)};
          }        
        `;
      }
      return res;
    })()};
  }
  
  --anim-pie-chart: 0;
    
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    top: 2.5rem;
    bottom: 2.5rem;
    left: 2.5rem;
    right: 2.5rem;
    background: ${Colors.white};
  }
`;

const ChartViewText = styled(TextTiny)<{ tooltip: string }>`
  pointer-events: auto;
  cursor: help;

  &:before {
    content: ${({ tooltip }) => `"${tooltip}"`};
    position: absolute;
    opacity: 0;
    
    margin: 0 auto 1.5rem;
    bottom: 50%;
    left: 50%;
    transform: translateX(-50%);
    pointer-events: none;
    
    transition: all 150ms ease-out;
    padding: .5rem;
    border-radius: 5px;
    box-shadow: 2px 2px 1px ${Colors.grayDark};
    color: ${Colors.grayDark};
    background: ${Colors.grayLight};
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

export interface Proportion {
  color?: string;
  title?: string;
  percentage: number;
}
interface Props {
  proportions: Proportion[];
  visible?: boolean;
}
export const PieChart: FC<Props> = ({ proportions, visible = true, ...props }) => {
  const chartViewRef = useRef<HTMLDivElement | null>(null);
  const [animateChartView, setAnimateChartView] = useState(false);
  const visibleRef = useRef<boolean>(visible);

  const updateView = (ready: boolean) => {
    if (!animateChartView && ready) {
      const scrollView = document.querySelector('main.app') as HTMLDivElement;
      const pageBottom = scrollView?.scrollTop + window.innerHeight;
      const chartHeight = chartViewRef.current?.parentElement?.offsetHeight;
      let offsetParent = chartViewRef.current;
      let chartBottom = 0;
      do {
        if (offsetParent) {
          chartBottom += offsetParent.offsetTop;
        }
        offsetParent = offsetParent?.offsetParent as HTMLDivElement;
      } while (offsetParent && offsetParent !== scrollView);

      if (pageBottom > chartBottom + (2*chartHeight / 3)) {
        setAnimateChartView(true);
      }
    }
  }

  useEffect(() => {
    const listener = (event: any) => {
      updateView(visibleRef.current);
    };

    setAnimateChartView(false);

    const scrollView = document.querySelector('main.app') as HTMLDivElement;
    if (scrollView) {
      scrollView.addEventListener('scroll', listener);
    }
    return () => {
      if (scrollView) {
        scrollView.removeEventListener('scroll', listener);
      }
    }
  }, []);

  useEffect(() => {
    visibleRef.current = visible;
    updateView(visibleRef.current);
  }, [visible]);

  const gradientConfig: string[] = [];
  const percentageRanges: Array<{ start: number, end: number }> = [];
  proportions.reduce((prev, proportion) => {
    const percentage = prev.percentage + proportion.percentage;
    const color = proportion.color || Colors.secondary;
    gradientConfig.push(`${color} 0, ${color} calc(${percentage.toFixed(1)}% * var(--anim-pie-chart,1))`);
    percentageRanges.push({ start: prev.percentage, end: percentage });
    return { percentage };
  }, { percentage: 0 });

  return (
    <SquareWrapper>
      <ChartView ref={chartViewRef} style={{ background: `conic-gradient(${gradientConfig.join(', ')}, #fff 0)` }} animationRunning={animateChartView}>
        {
          proportions.map((item, index) => {
            const range = percentageRanges[index];

            const percentageStart = range.start > 50 ? 1 - (range.start-50)/50 : range.start/50;
            const percentageEnd = range.end > 50 ? 1 - (range.end-50)/50 : range.end/50;

            const percentageYRaw = percentageStart + (percentageEnd - percentageStart)/2;
            const percentageYRawRight = percentageStart + (1 - percentageStart)/2;
            const percentageYRawLeft = percentageEnd + (1 - percentageEnd)/2;
            const percentageY = range.start < 50 && range.end > 50
              ? Math.min(percentageYRawLeft, percentageYRawRight) + (1 - Math.max(percentageYRawLeft, percentageYRawRight))
              : percentageYRaw;
            const percentageXRaw = percentageY > .5
              ? 1 - (percentageY - .5)
              : .5 + percentageY;
            const percentageX = percentageStart < percentageEnd ? percentageXRaw : 1 - percentageXRaw;

            // console.log("percentageY", percentageStart, percentageEnd, percentageY, percentageX );
            // console.log("percentage raw", percentageYRawLeft, percentageYRawRight);

            const style: React.CSSProperties = {
              top: '-1.5rem',
              bottom: '-1.5rem',
              left: '-1.5rem',
              right: '-1.5rem',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              transform: `translate(${Math.min(50, Math.max(-50, (percentageX * 100 - 50) * 1.75))}%, ${Math.min(50, Math.max(-50, (percentageY * 100 - 50) * 1.25))}%)`,
            };
            return (
              <FlexboxCenter style={{ position: 'absolute', pointerEvents: 'none', ...style }}>
                <ChartViewText tooltip={item.title}>{ item.percentage.toFixed(1) }%</ChartViewText>
              </FlexboxCenter>
            )
          })
        }
      </ChartView>
    </SquareWrapper>
  );
};
